import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cart as cartApi } from '../lib/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      setCart({ items: [], total: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const response = await cartApi.get();
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, sizes) => {
    try {
      setLoading(true);
      const response = await cartApi.add({ product_id: productId, sizes });
      setCart(response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (productId, sizes) => {
    try {
      setLoading(true);
      const response = await cartApi.update(productId, { product_id: productId, sizes });
      setCart(response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (productId) => {
    try {
      setLoading(true);
      const response = await cartApi.remove(productId);
      setCart(response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async () => {
    try {
      setLoading(true);
      await cartApi.clear();
      setCart({ items: [], total: 0 });
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const itemCount = cart.items.reduce((acc, item) => acc + item.total_quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart,
      fetchCart,
      itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
